import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { DeleteModal, ProfileDetails } from "../v3";
import { PurchaseService } from "../../services";
import { DELETE_VENDOR, EDIT_VENDOR } from "../../constants";
import { AddEditVendor } from "./AddEditVendor.component";
import { formatBalance, getBalanceColor } from "../../utils";
import { VENDOR_AVATAR } from "../../utils/image.constants";

export const VendorDetails = ({ onSuccess = () => {}, reloadData }) => {
  const [vendorDetails, setVendorDetails] = useState({});
  const [isActive, setIsActive] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    vendorId: null,
  });

  const { id } = useParams();
  const permissions = useSelector((state) => state.user.permissions);

  const hasVendorEdit = permissions.includes(EDIT_VENDOR);
  const hasVendorDelete = permissions.includes(DELETE_VENDOR);

  const navigate = useNavigate();

  useEffect(() => {
    fetchData(id);
  }, [id, reloadData]);

  const fetchData = async (vendorId) => {
    try {
      const { data } = await PurchaseService.getVendorById(vendorId);
      setVendorDetails(data);
      setIsActive(data?.active);
      onSuccess?.(data?.name);
    } catch (error) {
      toast.error("Failed to fetch vendor details");
      console.error("Error fetching vendor data:", error);
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const openDeleteModal = () => {
    setDeleteModal({
      isOpen: true,
      vendorId: id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      vendorId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await PurchaseService.deleteVendor(deleteModal.vendorId);
      toast("Vendor deleted successfully", {
        type: "success",
      });
      navigate(-1);
    } catch (error) {
      toast("Failed to delete vendor", {
        type: "error",
      });
      console.error("🚀 ~ deleteVendor ~ error:", error);
    }
  };

  const onActiveClick = async (e) => {
    const updatedStatus = e.target.checked;
    try {
      await PurchaseService.updateVendor(id, { active: updatedStatus });
      setIsActive(updatedStatus);
      toast.success("Status updated successfully");
    } catch (error) {
      toast.error("Failed to update status");
      console.error("🚀 ~ onActiveClick ~ error:", error);
    }
  };

  return (
    <>
      <ProfileDetails
        profileImage={VENDOR_AVATAR}
        name={vendorDetails?.name}
        phone={vendorDetails?.primaryPhone ? vendorDetails.primaryPhone : "not available"}
        isActive={isActive}
        canToggle={hasVendorEdit}
        canEdit={hasVendorEdit}
        canDelete={hasVendorDelete}
        onEdit={handleOpenModal}
        onDelete={openDeleteModal}
        onToggle={(e) => onActiveClick(e)}
        infoFields={[
          {
            label: "Balance",
            value: formatBalance(vendorDetails?.account?.balance),
            color: getBalanceColor(vendorDetails?.account?.balance),
          },
          { label: "Place", value: vendorDetails?.primaryAddress?.locality || "-" },
          { label: "GST Number", value: vendorDetails?.taxDetails?.gstNumber ? vendorDetails?.taxDetails?.gstNumber : "-" },
        ]}
      />

      {/* edit vendor modal  */}
      <AddEditVendor vendorId={id} open={openModal} onClose={handleCloseModal} onSuccess={() => fetchData(id)} />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this vendor?"
      />
    </>
  );
};
